/**
 * Business Configuration — Central catalog for multi-niche SaaS.
 *
 * This is the SINGLE source of truth for niche-specific:
 *   - terminology (client/service/appointment labels)
 *   - copy (landing headlines, tagline, feature texts)
 *   - default services (used on register / settings suggestions)
 *   - default theme accents (colors, hero image)
 *
 * The application ("Negocio de Servicios") stays generic; only this file
 * knows about each niche. To add a new niche (Spa, Veterinary, Coach,
 * Lawyer, Trainer, ...) append a new entry — NO other module needs edits.
 */

const IMG_BEAUTY =
  "https://images.unsplash.com/photo-1773904215697-e6c21fc27ac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYmVhdXR5JTIwc2Fsb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODUwOTE2OTR8MA&ixlib=rb-4.1.0&q=85";
const IMG_BARBER =
  "https://images.unsplash.com/photo-1585747860715-2ba37e788b70?auto=format&fit=crop&w=1600&q=80";
const IMG_LASHES =
  "https://images.unsplash.com/photo-1583241800698-9c2e0f8b1d1e?auto=format&fit=crop&w=1600&q=80";
const IMG_NAILS =
  "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=1600&q=80";
const IMG_PEDI =
  "https://images.unsplash.com/photo-1610992015732-2449b76344bc?auto=format&fit=crop&w=1600&q=80";
const IMG_DENTAL =
  "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=1600&q=80";
const IMG_DOCTOR =
  "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1600&q=80";

const DEFAULT_THEME = {
  primary: "#FFD700", // gold
  secondary: "#FF3AD2", // neon pink
  accent: "#00E5FF", // cyan
};

export const BUSINESS_TYPES = {
  beauty: {
    id: "beauty",
    name: "Estética",
    icon: "Sparkles",
    heroImage: IMG_BEAUTY,
    theme: DEFAULT_THEME,
    terminology: {
      client: "clienta",
      clientPlural: "clientas",
      service: "servicio",
      servicePlural: "servicios",
      appointment: "cita",
      appointmentPlural: "citas",
      professional: "estilista",
    },
    copy: {
      heroOverline: "Agenda · Cobra anticipo · Confirma por WhatsApp",
      heroBefore: "Menos",
      heroPain: "cancelaciones",
      heroConnector: "Más",
      heroSubject: "clientas",
      heroAfter: "pagando anticipo",
      heroTagline:
        "La agenda premium para salones, spas y estéticas. Tu clienta reserva, paga el anticipo y confirma por WhatsApp automáticamente.",
      featureAnticipo: "Define % de anticipo. Solo se agenda si tu clienta paga primero.",
      publicHeader: "Reserva tu cita",
    },
    defaultServices: [
      { name: "Uñas", price: 250, duration: 60 },
      { name: "Pestañas", price: 350, duration: 90 },
      { name: "Pedicura", price: 300, duration: 60 },
    ],
  },

  barber: {
    id: "barber",
    name: "Barbería",
    icon: "Scissors",
    heroImage: IMG_BARBER,
    theme: DEFAULT_THEME,
    terminology: {
      client: "cliente",
      clientPlural: "clientes",
      service: "corte",
      servicePlural: "cortes",
      appointment: "cita",
      appointmentPlural: "citas",
      professional: "barbero",
    },
    copy: {
      heroOverline: "Agenda · Cobra anticipo · Confirma por WhatsApp",
      heroBefore: "Menos",
      heroPain: "huecos",
      heroConnector: "Más",
      heroSubject: "clientes",
      heroAfter: "pagando su corte",
      heroTagline:
        "La agenda premium para barberías modernas. Tu cliente reserva, paga el anticipo y confirma por WhatsApp automáticamente.",
      featureAnticipo: "Define % de anticipo. Solo se agenda si tu cliente paga primero.",
      publicHeader: "Reserva tu cita",
    },
    defaultServices: [
      { name: "Corte clásico", price: 150, duration: 30 },
      { name: "Corte + Barba", price: 250, duration: 45 },
      { name: "Diseño de barba", price: 120, duration: 30 },
    ],
  },

  lashes: {
    id: "lashes",
    name: "Lashista",
    icon: "Eye",
    heroImage: IMG_LASHES,
    theme: DEFAULT_THEME,
    terminology: {
      client: "clienta",
      clientPlural: "clientas",
      service: "servicio",
      servicePlural: "servicios",
      appointment: "cita",
      appointmentPlural: "citas",
      professional: "lashista",
    },
    copy: {
      heroOverline: "Agenda · Cobra anticipo · Confirma por WhatsApp",
      heroBefore: "Menos",
      heroPain: "cancelaciones",
      heroConnector: "Más",
      heroSubject: "clientas",
      heroAfter: "pagando anticipo",
      heroTagline:
        "La agenda premium para lashistas. Tu clienta reserva, paga el anticipo y confirma por WhatsApp automáticamente.",
      featureAnticipo: "Define % de anticipo. Solo se agenda si tu clienta paga primero.",
      publicHeader: "Reserva tu cita",
    },
    defaultServices: [
      { name: "Extensiones clásicas", price: 400, duration: 120 },
      { name: "Extensiones volumen", price: 600, duration: 150 },
      { name: "Lifting de pestañas", price: 350, duration: 60 },
    ],
  },

  nails: {
    id: "nails",
    name: "Manicurista",
    icon: "Hand",
    heroImage: IMG_NAILS,
    theme: DEFAULT_THEME,
    terminology: {
      client: "clienta",
      clientPlural: "clientas",
      service: "servicio",
      servicePlural: "servicios",
      appointment: "cita",
      appointmentPlural: "citas",
      professional: "manicurista",
    },
    copy: {
      heroOverline: "Agenda · Cobra anticipo · Confirma por WhatsApp",
      heroBefore: "Menos",
      heroPain: "cancelaciones",
      heroConnector: "Más",
      heroSubject: "clientas",
      heroAfter: "pagando anticipo",
      heroTagline:
        "La agenda premium para manicuristas independientes. Tu clienta reserva, paga el anticipo y confirma por WhatsApp automáticamente.",
      featureAnticipo: "Define % de anticipo. Solo se agenda si tu clienta paga primero.",
      publicHeader: "Reserva tu cita",
    },
    defaultServices: [
      { name: "Manicura tradicional", price: 180, duration: 45 },
      { name: "Uñas acrílicas", price: 350, duration: 90 },
      { name: "Uñas de gel", price: 300, duration: 75 },
    ],
  },

  pedicure: {
    id: "pedicure",
    name: "Pedicurista",
    icon: "Footprints",
    heroImage: IMG_PEDI,
    theme: DEFAULT_THEME,
    terminology: {
      client: "clienta",
      clientPlural: "clientas",
      service: "servicio",
      servicePlural: "servicios",
      appointment: "cita",
      appointmentPlural: "citas",
      professional: "pedicurista",
    },
    copy: {
      heroOverline: "Agenda · Cobra anticipo · Confirma por WhatsApp",
      heroBefore: "Menos",
      heroPain: "cancelaciones",
      heroConnector: "Más",
      heroSubject: "clientas",
      heroAfter: "pagando anticipo",
      heroTagline:
        "La agenda premium para pedicuristas. Tu clienta reserva, paga el anticipo y confirma por WhatsApp automáticamente.",
      featureAnticipo: "Define % de anticipo. Solo se agenda si tu clienta paga primero.",
      publicHeader: "Reserva tu cita",
    },
    defaultServices: [
      { name: "Pedicura clásica", price: 250, duration: 60 },
      { name: "Pedicura spa", price: 400, duration: 90 },
      { name: "Pedicura con gel", price: 350, duration: 75 },
    ],
  },

  dental: {
    id: "dental",
    name: "Dentista",
    icon: "Stethoscope",
    heroImage: IMG_DENTAL,
    theme: DEFAULT_THEME,
    terminology: {
      client: "paciente",
      clientPlural: "pacientes",
      service: "tratamiento",
      servicePlural: "tratamientos",
      appointment: "cita",
      appointmentPlural: "citas",
      professional: "doctor",
    },
    copy: {
      heroOverline: "Agenda · Cobra anticipo · Confirma por WhatsApp",
      heroBefore: "Menos",
      heroPain: "cancelaciones",
      heroConnector: "Más",
      heroSubject: "pacientes",
      heroAfter: "confirmando su cita",
      heroTagline:
        "La agenda premium para consultorios dentales. Tu paciente reserva, paga el anticipo y confirma por WhatsApp automáticamente.",
      featureAnticipo: "Define % de anticipo. Solo se agenda si tu paciente paga primero.",
      publicHeader: "Reserva tu cita",
    },
    defaultServices: [
      { name: "Consulta y diagnóstico", price: 500, duration: 30 },
      { name: "Limpieza dental", price: 900, duration: 60 },
      { name: "Resina estética", price: 1200, duration: 60 },
    ],
  },

  doctor: {
    id: "doctor",
    name: "Médico General",
    icon: "HeartPulse",
    heroImage: IMG_DOCTOR,
    theme: DEFAULT_THEME,
    terminology: {
      client: "paciente",
      clientPlural: "pacientes",
      service: "consulta",
      servicePlural: "consultas",
      appointment: "cita",
      appointmentPlural: "citas",
      professional: "doctor",
    },
    copy: {
      heroOverline: "Agenda · Cobra anticipo · Confirma por WhatsApp",
      heroBefore: "Menos",
      heroPain: "cancelaciones",
      heroConnector: "Más",
      heroSubject: "pacientes",
      heroAfter: "confirmando su consulta",
      heroTagline:
        "La agenda premium para consultorios médicos. Tu paciente reserva, paga el anticipo y confirma por WhatsApp automáticamente.",
      featureAnticipo: "Define % de anticipo. Solo se agenda si tu paciente paga primero.",
      publicHeader: "Reserva tu cita",
    },
    defaultServices: [
      { name: "Consulta general", price: 600, duration: 30 },
      { name: "Consulta de seguimiento", price: 400, duration: 20 },
      { name: "Certificado médico", price: 300, duration: 15 },
    ],
  },
};

export const DEFAULT_BUSINESS_TYPE = "beauty";

/** Ordered list for selects/menus. */
export const BUSINESS_TYPE_LIST = Object.values(BUSINESS_TYPES);

/** Returns the resolved config for a business (or a raw type id). */
export function resolveBusinessConfig(businessOrType) {
  if (!businessOrType) return BUSINESS_TYPES[DEFAULT_BUSINESS_TYPE];
  const id =
    typeof businessOrType === "string"
      ? businessOrType
      : businessOrType.business_type;
  return BUSINESS_TYPES[id] || BUSINESS_TYPES[DEFAULT_BUSINESS_TYPE];
}

/** Terminology helper (shortcut). */
export function getTerm(businessOrType, key) {
  const cfg = resolveBusinessConfig(businessOrType);
  return cfg.terminology[key] || key;
}
